/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
	Box,
	TextField,
	FormLabel,
	FormHelperText,
	Container,
	Stack,
	Typography,
	CircularProgress,
	InputAdornment,
	Grid,
	Card,
	CardContent,
} from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	useDeleteUserSignatureMutation,
	useGetUserDetailsQuery,
	useUpdateUserProfileMutation,
	useUpdateUserSignatureMutation,
} from '@/features/user/userApi';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { debounce } from 'lodash';
import { NumericFormat } from 'react-number-format';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import SignatureEditor from '@/components/SignatureEditor';
import { UserEmailSelect } from '@/components/selects/UserEmailSelect';
import { DelegatedEmailsInput } from '@/components/DelegatedEmailEntry';
import { ProfileAvatarEditor } from '@/components/ProfileAvatarEditor';
import { UnlayerSignatureSettingsCard } from './UnlayerSignatureSettingsCard';

const PROFILE_KEYS = [
	'email',
	'phoneNumber',
	'linkedIn',
	'disclosureUrl',
	'aboutUser',
	'fspNumber',
	'imageUrl',
	'brokerSupportId',
	'claimsAdviserId',
	'complianceAdviserId',
	'displayEmail',
	// 'signature',
];

export const UserSettings = () => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });
	const [updateUserProfile, { isLoading: isLoadingUpdate }] = useUpdateUserProfileMutation();
	const [deleteUserSignature, { isLoading: isDeletingSignature }] =
		useDeleteUserSignatureMutation();
	const [updateUserSignature, { isLoading: isLoadingSignature }] =
		useUpdateUserSignatureMutation();
	const isLoading =
		isLoadingSignature || isLoadingUpdate || userQuery.isLoading || userQuery.isFetching;

	const [emailInitialised, setEmailInitialised] = useState(false);
	//country check
	const broker = userDetails?.broker;

	const { getFeatureFlags } = useFeatureFlagContext();
	const unlayerEmailFeature = Boolean(getFeatureFlags('unlayerEmails')?.active);

	const formik = useFormik({
		initialValues: {
			email: '',
			phoneNumber: '',
			linkedIn: '',
			imageUrl: '',
			aboutUser: '',
			fspNumber: '',
			brokerSupportId: '',
			claimsAdviserId: '',
			complianceAdviserId: '',
			disclosureUrl: '',
			signature: '',
			displayEmail: '',
			delegatedEmails: [],
			emailSignatures: [],
		},
	});

	const handleAutoSave = useMemo(
		() =>
			debounce(async (values = {}) => {
				try {
					console.log('values submitted: ', values);

					if (values.updatedSignature) {
						await updateUserSignature({
							email,
							html: values.updatedSignature?.signature,
							name: values.updatedSignature?.name,
							currentName: values.updatedSignature?.name,
						}).unwrap();
					} else {
						await updateUserProfile({
							email,
							properties: {
								...values,
							},
						}).unwrap();
					}

					dispatch(showToast({ message: 'Changes saved automatically', success: true }));
				} catch (err) {
					console.error('Auto-save failed:', err);

					dispatch(
						showToast({
							message:
								'There was an issue saving changes; please refresh and try again.',
							error: true,
						}),
					);
				}
			}, 500),
		[email, updateUserProfile, updateUserSignature, dispatch],
	);

	const removeDelegatedEmail = async (e) => {
		const newDelegatedEmails = formik.values.delegatedEmails.filter(
			(delegatedEmail) => delegatedEmail !== e,
		);
		await updateUserProfile({
			email,
			properties: {
				delegatedEmails: newDelegatedEmails,
			},
		}).unwrap();
		await deleteUserSignature({
			name: e,
		}).unwrap();
	};

	const addDelegatedEmail = async (e) => {
		const currentEmails = formik.values.delegatedEmails ?? [];
		if (currentEmails.includes(e)) return;

		const newDelegatedEmails = [...currentEmails, e];

		await updateUserProfile({
			email,
			properties: {
				delegatedEmails: newDelegatedEmails,
			},
		}).unwrap();
	};

	useEffect(() => {
		return () => handleAutoSave.cancel();
	}, [handleAutoSave]);

	useEffect(() => {
		if (userDetails) {
			const primaryEmail = (() => {
				const proxy = userDetails.proxyAddresses ?? [];
				const primary = proxy.find((addr) => addr.startsWith('SMTP:')) ?? '';
				return primary.replace(/^SMTP:/i, '').trim() || userDetails.email || '';
			})();
			const emailSignatures =
				userDetails.emailSignatures && userDetails.emailSignatures.length > 0
					? userDetails.emailSignatures
					: [];

			const defaultSignature =
				emailSignatures.find((sig) => sig.name === primaryEmail) ||
				emailSignatures.find((sig) => sig.name === email) ||
				null;
			formik.setValues({
				email: userDetails.email ?? '',
				phoneNumber: userDetails.phoneNumber ?? '',
				linkedIn: userDetails.linkedIn ?? '',
				disclosureUrl: userDetails.disclosureUrl ?? '',
				aboutUser: userDetails.aboutUser ?? '',
				fspNumber: userDetails.fspNumber ?? '',
				imageUrl: userDetails.imageUrl ?? '',
				brokerSupportId: userDetails.brokerSupportId ?? '',
				claimsAdviserId: userDetails.claimsAdviserId ?? '',
				complianceAdviserId: userDetails.complianceAdviserId ?? '',
				signature: defaultSignature?.signature ?? '',
				displayEmail: userDetails.displayEmail ?? primaryEmail,
				delegatedEmails: userDetails.delegatedEmails ?? [],
				emailSignatures,
				selectedSignature: defaultSignature,
			});

			setEmailInitialised(true);
		}
	}, [userDetails]);

	const handleFieldBlur = (e) => {
		const vals = PROFILE_KEYS.reduce((acc, val) => {
			console.log('val', val, userDetails[val]);
			if (formik.values[val] !== userDetails[val]) {
				acc.push(val);
			}
			return acc;
		}, []);
		formik.handleBlur(e);
		if (vals.length > 0) {
			const values = vals.reduce((acc, val) => {
				acc[val] = formik.values[val];
				return acc;
			}, {});
			handleAutoSave(values);
		}
	};

	const handleSelectChange = (fieldName) => (user) => {
		const value = user?.id ?? null;
		formik.setFieldValue(fieldName, value);
		handleAutoSave({ [fieldName]: value });
	};

	return (
		<Container>
			<Stack spacing={2}>
				<Stack spacing={2} alignItems={'center'} justifyContent={'center'} pb={3}>
					<ProfileAvatarEditor
						userId={userDetails?.userId}
						email={email}
						imageUrl={formik.values.imageUrl}
						size={200}
						onImageUrlChange={(downloadUrl) =>
							formik.setFieldValue('imageUrl', downloadUrl)
						}
					/>

					<Typography variant='broker_header'>{email}</Typography>
				</Stack>

				<Grid container spacing={3}>
					<Grid size={4}>
						<FormLabel>Primary Email</FormLabel>
						<Box sx={{ position: 'relative' }}>
							<UserEmailSelect
								disabled={isLoading}
								loading={!emailInitialised}
								minimal={false}
								value={formik.values.displayEmail}
								delegatedEmails={formik.values.delegatedEmails}
								onChange={(selectedEmail) => {
									formik.setFieldValue('displayEmail', selectedEmail);

									handleAutoSave({ displayEmail: selectedEmail });
								}}
							/>
							{isLoading && (
								<CircularProgress
									size={24}
									sx={{
										position: 'absolute',
										top: '50%',
										right: 8,
										marginTop: '-12px',
									}}
								/>
							)}
						</Box>
						<FormHelperText sx={{ marginLeft: 0 }}>
							This email will display in your Statement of Advice PDF
						</FormHelperText>
					</Grid>
					<Grid size={4}>
						<FormLabel>{broker?.country === 'AU' ? 'AFS' : 'FSP'} Number</FormLabel>
						<NumericFormat
							customInput={TextField}
							decimalScale={0}
							fixedDecimalScale
							allowNegative={false}
							allowLeadingZeros
							fullWidth
							onValueChange={({ value }) => {
								formik.setFieldValue('fspNumber', value);
							}}
							{...formik.getFieldProps('fspNumber')}
							onBlur={handleFieldBlur}
							error={formik.touched.fspNumber && Boolean(formik.errors.fspNumber)}
							helperText={formik.touched.fspNumber && formik.errors.fspNumber}
							slotProps={{
								input: {
									sx: {
										'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
											{
												WebkitAppearance: 'none',
												margin: 0,
											},
									},
									startAdornment: (
										<InputAdornment position='start'>
											{broker?.country === 'AU' ? 'AFS' : 'FSP'}
										</InputAdornment>
									),
									endAdornment: isLoading && (
										<InputAdornment position='end'>
											<CircularProgress size={20} />
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<FormLabel>Phone Number</FormLabel>
						<NumericFormat
							customInput={TextField}
							decimalScale={0}
							fixedDecimalScale
							allowNegative={false}
							allowLeadingZeros
							fullWidth
							onValueChange={({ value }) => {
								formik.setFieldValue('phoneNumber', value);
							}}
							{...formik.getFieldProps('phoneNumber')}
							onBlur={handleFieldBlur}
							error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
							helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
							slotProps={{
								input: {
									sx: {
										'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
											{
												WebkitAppearance: 'none',
												margin: 0,
											},
									},
									endAdornment: isLoading && (
										<InputAdornment position='end'>
											<CircularProgress size={20} />
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
					<Grid size={12}>
						{' '}
						<DelegatedEmailsInput
							formik={formik}
							onAddEmail={(email) => {
								console.log('added email: ', email);
								addDelegatedEmail(email);
							}}
							onRemoveEmail={(email) => {
								console.log('removed email: ', email);
								removeDelegatedEmail(email);
							}}
							disabled={isLoading}
							loading={!emailInitialised}
						/>{' '}
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid size={6}>
						<FormLabel>LinkedIn Profile URL</FormLabel>
						<TextField
							fullWidth
							variant='outlined'
							{...formik.getFieldProps('linkedIn')}
							onBlur={handleFieldBlur}
							error={formik.touched.linkedIn && Boolean(formik.errors.linkedIn)}
							helperText={formik.touched.linkedIn && formik.errors.linkedIn}
							slotProps={{
								input: {
									endAdornment: isLoading && (
										<InputAdornment position='end'>
											<CircularProgress size={20} />
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
					<Grid size={6}>
						<FormLabel>Disclosure Statement</FormLabel>
						<TextField
							fullWidth
							variant='outlined'
							{...formik.getFieldProps('disclosureUrl')}
							onBlur={handleFieldBlur}
							error={
								formik.touched.disclosureUrl && Boolean(formik.errors.disclosureUrl)
							}
							helperText={formik.touched.disclosureUrl && formik.errors.disclosureUrl}
							slotProps={{
								input: {
									endAdornment: isLoading && (
										<InputAdornment position='end'>
											<CircularProgress size={20} />
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid size={12}>
						<FormLabel>About My Services</FormLabel>
						<TextField
							fullWidth
							variant='outlined'
							name='aboutUser'
							multiline
							rows={4}
							{...formik.getFieldProps('aboutUser')}
							onBlur={handleFieldBlur}
							error={formik.touched.aboutUser && Boolean(formik.errors.aboutUser)}
							helperText={formik.touched.aboutUser && formik.errors.aboutUser}
							slotProps={{
								input: {
									endAdornment: isLoading && (
										<InputAdornment position='end'>
											<CircularProgress size={20} />
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
				</Grid>

				<Stack>
					<FormLabel>My Service Team</FormLabel>
					<FormHelperText sx={{ marginLeft: 0 }}>
						The following team members will appear on your generated Statement of Advice
					</FormHelperText>
				</Stack>

				<Grid container spacing={2} sx={{ mb: 5 }}>
					{['brokerSupportId', 'claimsAdviserId', 'complianceAdviserId'].map(
						(field, index) => (
							<Grid size={4} key={field}>
								<FormLabel>
									{field === 'brokerSupportId' && 'Broker Support'}
									{field === 'claimsAdviserId' && 'Claims Adviser'}
									{field === 'complianceAdviserId' && 'Compliance Adviser'}
								</FormLabel>
								<Box sx={{ position: 'relative' }}>
									<OwnerSelect
										initialId={formik.values[field]}
										onChange={handleSelectChange(field)}
										disabled={isLoading}
										allowRemoval={true}
										showGroup={true}
									/>
									{isLoading && (
										<CircularProgress
											size={24}
											sx={{
												position: 'absolute',
												top: '50%',
												right: 8,
												marginTop: '-12px',
											}}
										/>
									)}
								</Box>
							</Grid>
						),
					)}
				</Grid>
				{unlayerEmailFeature ? (
					<UnlayerSignatureSettingsCard disabled={isLoading} />
				) : (
					<Card variant='outlined' sx={{ mb: 3, backgroundColor: 'transparent' }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid size={8}>
									<FormLabel>Email Signatures</FormLabel>
									<Box sx={{ position: 'relative' }}>
										<UserEmailSelect
											disabled={isLoading}
											value={formik.values.selectedSignature ?? null}
											delegatedEmails={formik.values.delegatedEmails}
											onChange={(selectedEmailObj) => {
												const matchedSignature =
													formik.values.emailSignatures.find(
														(sig) =>
															sig.name === selectedEmailObj?.name,
													) || {
														name: selectedEmailObj?.name,
														signature: '',
													};

												formik.setFieldValue(
													'selectedSignature',
													matchedSignature,
												);
												formik.setFieldValue(
													'signature',
													matchedSignature.signature ?? '',
												);
											}}
										/>

										{isLoading && (
											<CircularProgress
												size={24}
												sx={{
													position: 'absolute',
													top: '50%',
													right: 8,
													marginTop: '-12px',
												}}
											/>
										)}
									</Box>

									<FormHelperText sx={{ marginLeft: 1.5 }}>
										Preview created signatures against your email addresses
									</FormHelperText>
								</Grid>
							</Grid>
							<SignatureEditor
								key={formik.values.selectedSignature?.name}
								url={formik.values.selectedSignature?.signature}
								value={formik.values.signature}
								setValue={(value) => formik.setFieldValue('signature', value)}
								disabled={isLoading}
								handleSave={() => {
									const current = formik.values.selectedSignature;
									if (!current) return;

									if (
										JSON.stringify(formik.values.signature) ===
										JSON.stringify(current.signature)
									) {
										return;
									}

									const updatedSignature = {
										...current,
										signature: formik.values.signature,
									};
									handleAutoSave({
										updatedSignature: {
											...updatedSignature,
											name: formik.values.selectedSignature?.name,
										},
									});
								}}
								showLabel={false}
							/>
						</CardContent>
					</Card>
				)}
			</Stack>
		</Container>
	);
};
